import React, { useState, useContext } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import axios from '../../utils/axios.utils';
import Button from '@mui/material/Button';
import { DeleteConfirmationModalContext } from '../../contexts/DeleteConfirmationModalContext';

interface EditorToggleProps {
  afterUpdate: () => void;
  record: { [key: string]: any; id?: string | number };
  controller: string;
  field: string;
  allowDelete?: boolean;
  onLabelClick?: () => void;
  selected?: boolean;
}

const EditorToggle: React.FC<EditorToggleProps> = (props) => {
  const { afterUpdate, record, controller, field, allowDelete, onLabelClick, selected } = props;

  const {
    setModelLabel,
    setDeleteConfirmationModalOpen,
    setRecordToDelete: setContextRecordToDelete,
    setDeleteURL,
  } = useContext(DeleteConfirmationModalContext);

  let [isEditing, setIsEditing] = useState(false);

  const value = record?.[field] || '';
  const [inputValue, setInputValue] = useState(value);

  let [errorMessage, setErrorMessage] = useState('');

  const updateRecord = async () => {
    setErrorMessage('');

    try {
      await axios.put(`/${controller}/${record?.id}`, { [`${field}`]: inputValue });
      afterUpdate();
      setIsEditing(false);
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.response?.data?.message || 'An unknown error occurred';
      setErrorMessage(message);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      updateRecord();
    }
  };

  const handleDeleteClick = () => {
    setModelLabel(field);
    setDeleteURL(controller);
    setContextRecordToDelete(record);
    setDeleteConfirmationModalOpen(true);
  };

  return isEditing ? (
    <Grid container spacing={2} alignItems="center" onKeyDown={handleKeyPress}>
      <Grid item xs>
        <TextField
          type="text"
          label=""
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          fullWidth
          autoFocus
          onKeyDown={handleKeyPress}
        />
      </Grid>
      <Grid item>
        <Grid container spacing={1}>
          <Grid item>
            <Button
              variant="contained"
              onClick={updateRecord}
              disabled={inputValue === record?.[field]}
              sx={{
                backgroundColor: '#72a295',
                '&:hover': {
                  backgroundColor: '#649789',
                },
                minWidth: 'auto',
                padding: '8px',
              }}
            >
              <SaveIcon fontSize="small" />
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={() => {
                setIsEditing(false);
              }}
              sx={{
                minWidth: 'auto',
                padding: '8px',
                borderColor: 'grey.400',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'grey.400',
                  backgroundColor: 'grey.50',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </Button>
          </Grid>
        </Grid>
      </Grid>
      {errorMessage && (
        <Grid item xs={12}>
          <Typography variant="body2" color="error">
            {errorMessage}
          </Typography>
        </Grid>
      )}
    </Grid>
  ) : (
    <Grid container spacing={1} alignItems="center">
      <Grid item>
        <Grid container spacing={1} alignItems="center">
          {allowDelete && (
            <Grid item>
              <Button
                variant="contained"
                onClick={handleDeleteClick}
                sx={{
                  backgroundColor: '#cd5c5c',
                  '&:hover': {
                    backgroundColor: '#b84a4a',
                  },
                  minWidth: 'auto',
                  padding: '8px',
                }}
              >
                <DeleteIcon fontSize="small" />
              </Button>
            </Grid>
          )}
          <Grid item>
            <Button
              variant="outlined"
              onClick={() => {
                setIsEditing(true);
              }}
              sx={{
                backgroundColor: '#fff9f1',
                borderColor: 'grey.400',
                color: 'text.primary',
                minWidth: 'auto',
                padding: '8px',
                marginLeft: allowDelete ? 0 : '16px',
                '&:hover': {
                  borderColor: 'grey.400',
                  backgroundColor: '#fff5e6',
                },
              }}
            >
              <EditIcon fontSize="small" />
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs>
        <Box
          onClick={onLabelClick}
          sx={{
            padding: '12px',
            minWidth: '80%',
            border: onLabelClick ? '1px solid' : 'none',
            borderColor: onLabelClick ? 'grey.400' : 'transparent',
            borderRadius: onLabelClick ? '4px' : 0,
            cursor: !selected && onLabelClick ? 'pointer' : 'default',
            backgroundColor: selected ? 'rgba(139,196,181,0.6)' : 'white',
          }}
        >
          <Typography variant="h6" component="h5" sx={{ fontSize: '20px', fontWeight: 400 }}>
            {value}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

export default EditorToggle;
